import { useApi, useToaster } from "@shopexperts/shared";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import type { UpdatePlanBillingOptionRequest } from "@shopexperts/legacy-api";
import { extractBillingApiMessage } from "./billingOptionForm";

export function useUpdateBillingOption() {
  const api = useApi();
  const toaster = useToaster();
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isPending, error } = useMutation({
    mutationKey: ["admin", "expert-plans", "billing-options", "update"],
    mutationFn: ({
      planId,
      billingOptionId,
      data,
    }: {
      planId: number;
      billingOptionId: number;
      data: UpdatePlanBillingOptionRequest;
    }) => {
      return api.admin.expertBilling.updateBillingOption({
        params: { plan: planId, billingOption: billingOptionId },
        body: data,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "expert-plans"] });
      toaster.create({
        title: "Billing option updated",
        type: "success",
      });
    },
    onError: (err: Error) => {
      // Active edits and activation synchronize inside the update request.
      // A provider failure still persists `failed` + inactive server-side, so
      // refresh the workbench even though Axios rejected the 502 response.
      queryClient.invalidateQueries({ queryKey: ["admin", "expert-plans"] });
      toaster.create({
        title: "Billing option needs attention",
        description: extractBillingApiMessage(err) ?? "An unexpected error occurred",
        type: "error",
      });
      console.error("[useUpdateBillingOption] Error:", err);
    },
  });

  return { mutate, mutateAsync, isPending, error };
}

export default useUpdateBillingOption;
